// SpaceCard — web mirror of /app/mobile/src/components/SpaceCard.jsx.
//
// Creates a Family or Flatmate shared space, lets users join via 8-char
// code, surfaces invite code + cartiq360://join-space deep-link for
// sharing, and supports rotate/leave.
//
// Strict Clean Wellness theming: uses tokens (text-[#334155], bg-white,
// border-[#E2E8F0], --cartiq-primary mint). Caller must mount this
// component inside a `.cartiq-wellness` scope (Dashboard already does).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Users, Home, Plus, Copy, RotateCw, LogOut, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TYPES = [
  {
    key: "family",
    label: "Family",
    Icon: Users,
    blurb: "Pool spend, share a budget. No bill-splitting.",
  },
  {
    key: "flatmates",
    label: "Flatmates",
    Icon: Home,
    blurb: "Splitwise-style settle-up. Who paid what.",
  },
];

function buildJoinUrl(code) {
  return `cartiq360://join-space?code=${encodeURIComponent(code)}`;
}

export default function SpaceCard({ onChange }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [selectedType, setSelectedType] = useState("family");
  const [leaveOpen, setLeaveOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/spaces/me");
      setState(data);
      if (onChange) onChange(data);
    } catch (e) {
      // Soft fail — user might not be authed; component just hides.
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [onChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/spaces/create", {
        type: selectedType,
        name: selectedType === "flatmates" ? "Our Flat" : "Our Household",
      });
      setState(data);
      if (onChange) onChange(data);
      toast.success(
        selectedType === "flatmates"
          ? "Flatmate space ready. Share the code to start splitting bills."
          : "Family space ready. Share the code to pool your spend.",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  }, [selectedType, onChange]);

  const handleJoin = useCallback(async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 8) {
      toast.error("Invite codes are 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/spaces/join", { code });
      setState(data);
      setJoinCode("");
      if (onChange) onChange(data);
      toast.success("Joined!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Join failed");
    } finally {
      setBusy(false);
    }
  }, [joinCode, onChange]);

  const handleRotate = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/spaces/rotate-code");
      setState(data);
      toast.success("New invite code generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotate failed");
    } finally {
      setBusy(false);
    }
  }, []);

  const handleLeave = useCallback(async () => {
    const isPrimary = state?.role === "primary";
    setBusy(true);
    try {
      await api.post("/spaces/leave");
      await refresh();
      toast.success(isPrimary ? "Space dissolved." : "You left the space.");
      setLeaveOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  }, [refresh, state]);

  const inviteUrl = useMemo(
    () => (state?.inviteCode ? buildJoinUrl(state.inviteCode) : null),
    [state?.inviteCode],
  );

  const copyToClipboard = useCallback((text, label) => {
    if (!text || !navigator?.clipboard) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed — long-press to select"),
    );
  }, []);

  if (loading) {
    return (
      <div
        className="cartiq-card flex items-center gap-3 text-sm text-[#64748B]"
        data-testid="space-card-loading"
      >
        <div className="w-2 h-2 rounded-full bg-[#3EB489] animate-pulse" />
        Loading space…
      </div>
    );
  }

  // No space yet — show create (with type toggle) + join paths
  if (!state?.isSpace) {
    return (
      <div className="cartiq-card" data-testid="space-card-empty">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#3EB489]" />
          <h3 className="font-display uppercase text-base text-[#0F172A]">
            Shared Space
          </h3>
        </div>
        <p className="mt-2 text-sm text-[#64748B] leading-relaxed">
          Bundle up to 4 members. Family pools the budget; Flatmates splits the
          bill.
        </p>

        {/* Type toggle */}
        <div
          className="mt-4 flex gap-1 p-1 rounded-full bg-[#F1F5F9]"
          role="tablist"
        >
          {TYPES.map((t) => {
            const active = selectedType === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`space-type-${t.key}`}
                onClick={() => setSelectedType(t.key)}
                className={`flex-1 inline-flex items-center justify-center gap-2 py-2 px-3 rounded-full text-[12px] font-bold uppercase tracking-[0.12em] transition-colors ${
                  active
                    ? "bg-[#3EB489] text-white shadow-sm"
                    : "text-[#64748B] hover:text-[#334155]"
                }`}
              >
                <t.Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-[#64748B] text-center italic">
          {TYPES.find((t) => t.key === selectedType)?.blurb}
        </p>

        <button
          type="button"
          disabled={busy}
          onClick={handleCreate}
          data-testid="space-create-btn"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#3EB489] hover:bg-[#2D8C6E] text-white text-[12px] font-bold uppercase tracking-[0.14em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          Create {selectedType === "flatmates" ? "Flatmate" : "Family"} Space
        </button>

        <div className="mt-5 flex items-center gap-3">
          <div className="flex-1 h-px bg-[#E2E8F0]" />
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#94A3B8]">
            Or Join One
          </div>
          <div className="flex-1 h-px bg-[#E2E8F0]" />
        </div>

        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={joinCode}
            onChange={(e) =>
              setJoinCode(e.target.value.toUpperCase().slice(0, 8))
            }
            placeholder="ENTER 8-CHAR CODE"
            disabled={busy}
            data-testid="space-join-input"
            maxLength={8}
            className="flex-1 bg-white border border-[#E2E8F0] rounded-lg px-3 py-3 text-center text-[#334155] font-extrabold tracking-[0.3em] uppercase placeholder:text-[#CBD5E1] placeholder:tracking-[0.12em] focus:outline-none focus:border-[#3EB489] focus:ring-2 focus:ring-[#3EB489]/20"
          />
          <button
            type="button"
            onClick={handleJoin}
            disabled={busy || joinCode.length !== 8}
            data-testid="space-join-btn"
            className="px-5 rounded-lg bg-[#2D8C6E] hover:bg-[#226B54] text-white text-[12px] font-bold uppercase tracking-[0.14em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Join
          </button>
        </div>
      </div>
    );
  }

  // Active space
  const isPrimary = state.role === "primary";
  const isFlatmates = state.type === "flatmates";
  return (
    <>
      <div className="cartiq-card" data-testid="space-card-active">
      <div className="flex items-center gap-2">
        {isFlatmates ? (
          <Home className="w-4 h-4 text-[#3EB489]" />
        ) : (
          <Users className="w-4 h-4 text-[#3EB489]" />
        )}
        <h3 className="font-display uppercase text-base text-[#0F172A] flex-1">
          {state.name || "Shared Space"}
        </h3>
        <span
          data-testid="space-type-badge"
          className={`px-2 py-1 rounded-full text-[9px] font-extrabold uppercase tracking-[0.18em] ${
            isFlatmates
              ? "bg-blue-50 text-blue-700"
              : "bg-[#3EB489]/10 text-[#2D8C6E]"
          }`}
        >
          {isFlatmates ? "Flatmates" : "Family"}
        </span>
      </div>

      {isPrimary && state.inviteCode ? (
        <div
          className="mt-3 p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]"
          data-testid="space-invite-code"
        >
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
                Invite Code
              </div>
              <div className="text-xl font-extrabold tracking-[0.32em] text-[#0F172A] mt-1 select-all">
                {state.inviteCode}
              </div>
            </div>
            <button
              type="button"
              onClick={() => copyToClipboard(state.inviteCode, "Code")}
              data-testid="space-copy-code-btn"
              className="p-2 rounded-md hover:bg-white border border-transparent hover:border-[#E2E8F0] text-[#3EB489]"
              aria-label="Copy code"
              title="Copy code"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleRotate}
              disabled={busy}
              data-testid="space-rotate-btn"
              className="p-2 rounded-md hover:bg-white border border-transparent hover:border-[#E2E8F0] text-[#64748B] hover:text-[#2D8C6E] disabled:opacity-40"
              aria-label="Rotate code"
              title="Rotate code"
            >
              <RotateCw className="w-4 h-4" />
            </button>
          </div>
          {inviteUrl ? (
            <button
              type="button"
              onClick={() => copyToClipboard(inviteUrl, "Deep link")}
              data-testid="space-invite-deeplink"
              className="mt-2 w-full text-left text-[11px] font-mono text-[#2D8C6E] truncate hover:underline"
              title={inviteUrl}
            >
              {inviteUrl}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-[#64748B]">
        Members ({state.memberCount}/{state.max})
      </div>
      <ul className="mt-2 space-y-0">
        {(state.members || []).map((m) => (
          <li
            key={m.user_id}
            className="flex items-center gap-3 py-2 border-b border-[#E2E8F0] last:border-b-0"
            data-testid={`space-member-${m.user_id}`}
          >
            <div className="w-8 h-8 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[#3EB489] font-extrabold text-sm">
              {(m.name || "?")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-[#334155] truncate">
                {m.name}
              </div>
              <div className="text-[11px] text-[#64748B] truncate">{m.email}</div>
            </div>
            {m.role === "primary" ? (
              <span className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-[#2D8C6E]">
                Primary
              </span>
            ) : null}
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => setLeaveOpen(true)}
        disabled={busy}
        data-testid="space-leave-btn"
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-[#FB7185] text-[#E11D48] text-[12px] font-bold uppercase tracking-[0.14em] transition-colors hover:bg-[#FB7185]/5 disabled:opacity-50"
      >
        <LogOut className="w-4 h-4" />
        {isPrimary ? "Dissolve Space" : "Leave Space"}
      </button>
    </div>

    <AlertDialog
      open={leaveOpen}
      onOpenChange={(o) => {
        if (!busy) setLeaveOpen(o);
      }}
    >
      {/* Radix portals AlertDialogContent to <body>, so the .cartiq-wellness
          class must live on the content node itself (not a DOM parent) for
          our scoped CSS tokens to cascade in. Don't drop it. */}
      <AlertDialogContent
        className="cartiq-wellness bg-white border border-[#E2E8F0] rounded-2xl shadow-2xl p-0 overflow-hidden max-w-md"
        data-testid="space-leave-dialog"
      >
        <div className="p-6 flex flex-col gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(251,113,133,0.12)" }}
          >
            <AlertTriangle className="w-6 h-6 text-[#FB7185]" />
          </div>
          <AlertDialogHeader className="space-y-2 text-left">
            <AlertDialogTitle
              className="text-[#0F172A] text-xl font-extrabold tracking-tight"
              data-testid="space-leave-dialog-title"
            >
              {isPrimary ? "Dissolve this space?" : "Leave this space?"}
            </AlertDialogTitle>
            <AlertDialogDescription
              className="text-[#64748B] text-sm leading-relaxed"
              data-testid="space-leave-dialog-description"
            >
              {isPrimary
                ? "This will unlink every member and delete the space permanently. Member spend data stays in their own accounts, but the shared pool is gone."
                : "Your spend will no longer roll up here. You can rejoin later with a fresh invite code from the primary member."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 sm:gap-2">
            <AlertDialogCancel
              disabled={busy}
              data-testid="space-leave-cancel"
              className="cartiq-btn-ghost h-11 px-5 rounded-lg bg-[#F1F5F9] border border-[#E2E8F0] text-[#334155] font-bold text-[12px] uppercase tracking-[0.14em] hover:bg-[#E2E8F0] hover:text-[#0F172A]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={handleLeave}
              data-testid="space-leave-confirm"
              className="h-11 px-5 rounded-lg bg-[#FB7185] hover:bg-[#E11D48] text-white font-bold text-[12px] uppercase tracking-[0.14em] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? "Working…" : isPrimary ? "Dissolve" : "Leave"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
}
